// ════════════════════════════════════════════════════════════════════════════
// CuentaTab — ficha completa de una cuenta (antes «Estadísticas»).
//
// Una sola pantalla con: datos web y de juego, totales (€ pagados, ❄ gastados,
// bonos, votos), personajes con sus acciones GM, personajes eliminados e
// historial unificado (recargas, compras con su estado de entrega, movimientos
// de cristales y de bonos). Datos: GET /api/admin/accounts/{u}/overview + stats
// + deleted-chars.
// ════════════════════════════════════════════════════════════════════════════
import React, { useEffect, useRef, useState } from 'react';
import {
  Users, AlertCircle, CheckCircle, Clock, Sword, ScrollText, Trophy, Coins, Search, KeyRound, Copy,
  ExternalLink, Ban, ChevronDown, ChevronRight, RefreshCw,
} from 'lucide-react';
import axios from 'axios';
import { BACKEND_URL, apiErr, voucherText, VOUCHER_LABELS, REASON_LABELS, DELIVERY, STAT_CLASS_COLORS, STAT_CLASS_NAMES, STAT_RACE_NAMES, formatPlayed, GeoCell } from '../shared';
import { fmtDateTime, fmtEur, fmtNum } from '../../../lib/fmt';
import BanDialog from '../../../components/admin/BanDialog';

const GM_ACTIONS = [
  { key: 'rename',          label: 'Cambio de nombre',    color: '#60a5fa', desc: 'El jugador podrá elegir un nuevo nombre al hacer login.' },
  { key: 'customize',       label: 'Personalización',     color: '#a78bfa', desc: 'El jugador podrá modificar su apariencia al hacer login.' },
  { key: 'change_race',     label: 'Cambio de raza',      color: '#34d399', desc: 'El jugador podrá cambiar de raza al hacer login.' },
  { key: 'change_faction',  label: 'Cambio de facción',   color: '#fb923c', desc: 'El jugador podrá cambiar de facción al hacer login.' },
];

const VOUCHER_REASONS = {
  shop_purchase: 'Compra de bonos', redeem: 'Canje', redeem_refund: 'Bono devuelto (entrega fallida)', admin_grant: 'Admin',
};
const FILTERS = [
  ['all', 'Todo'], ['topup', 'Recargas'], ['purchase', 'Compras'], ['crystals', 'Cristales'], ['voucher', 'Bonos'],
];
const KIND_CHIP = {
  topup:    { label: 'RECARGA', color: '#fcd34d' },
  purchase: { label: 'COMPRA',  color: '#00e5ff' },
  crystals: { label: 'CRISTALES', color: '#a78bfa' },
  voucher:  { label: 'BONO',    color: '#fb923c' },
};

const signed = (n) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${fmtNum(Math.abs(n))}`;

export default function CuentaTab({ presetUsername, onConsumePreset, showFeedback }) {
  // Búsqueda
  const [query, setQuery]             = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIdx, setActiveIdx]     = useState(0);
  const searchSeq = useRef(0);
  // Cuenta cargada
  const [selected, setSelected]       = useState(null); // { username }
  const [data, setData]               = useState(null); // respuesta de /overview
  const [chars, setChars]             = useState([]);
  const [deletedChars, setDeletedChars] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [loadError, setLoadError]     = useState(null);
  const [filter, setFilter]           = useState('all');
  const [openEvent, setOpenEvent]     = useState(null);
  const [banModal, setBanModal]       = useState(null);
  // Acciones (bloques heredados de la antigua pestaña)
  const [confirm, setConfirm]         = useState(null); // { guid, name, action }
  const [applying, setApplying]       = useState(false);
  const [actionFeedback, setActionFeedback] = useState(null); // { ok, msg }
  const [restoreConfirm, setRestoreConfirm] = useState(null); // { guid, name }
  const [restoring, setRestoring]           = useState(false);
  const [tempPwdConfirm, setTempPwdConfirm] = useState(false);
  const [generatingTempPwd, setGeneratingTempPwd] = useState(false);
  const [tempPwdResult, setTempPwdResult]   = useState(null); // { username, temp_password }
  const [tempPwdCopied, setTempPwdCopied]   = useState(false);

  // Sugerencias (cuenta, email, cuenta de juego o personaje)
  useEffect(() => {
    const term = query.trim();
    if (term.length < 2 || term === selected?.username) { setSuggestions([]); setShowDropdown(false); return undefined; }
    const seq = ++searchSeq.current;
    const t = setTimeout(() => {
      axios.get(`${BACKEND_URL}/api/admin/accounts/search`, { params: { q: term }, withCredentials: true })
        .then(({ data: rows }) => {
          if (seq !== searchSeq.current) return;
          setSuggestions(Array.isArray(rows) ? rows : []); setActiveIdx(0); setShowDropdown(true);
        })
        .catch(() => setSuggestions([]));
    }, 220);
    return () => clearTimeout(t);
  }, [query, selected]);

  const loadAccount = async (username) => {
    if (!username) return;
    setSelected({ username });
    setQuery(username);
    setShowDropdown(false);
    setSuggestions([]);
    setLoading(true);
    setLoadError(null);
    setOpenEvent(null);
    const base = `${BACKEND_URL}/api/admin/accounts/${encodeURIComponent(username)}`;
    const [ov, st, del] = await Promise.allSettled([
      axios.get(`${base}/overview`,      { withCredentials: true }),
      axios.get(`${base}/stats`,         { withCredentials: true }),
      axios.get(`${base}/deleted-chars`, { withCredentials: true }),
    ]);
    if (ov.status === 'fulfilled') {
      setData(ov.value.data);
      setSelected({ username: ov.value.data.account.username });
      setQuery(ov.value.data.account.username);
    } else {
      setData(null);
      setLoadError(apiErr(ov.reason, 'No se pudo cargar la cuenta.'));
    }
    setChars(st.status === 'fulfilled' ? st.value.data : []);
    setDeletedChars(del.status === 'fulfilled' ? del.value.data : []);
    setLoading(false);
  };
  const reload = () => selected && loadAccount(selected.username);

  // Llega desde otra pestaña (clic en un usuario en Usuarios o Compras)
  useEffect(() => {
    if (!presetUsername) return;
    loadAccount(presetUsername);
    onConsumePreset && onConsumePreset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetUsername]);

  const onSearchKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Escape') setShowDropdown(false);
    else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = suggestions[activeIdx] || suggestions[0];
      loadAccount(pick ? pick.username : query.trim());
    }
  };

  const applyAtLogin = async () => {
    if (!confirm) return;
    setApplying(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/characters/${confirm.guid}/at-login`,
        { action: confirm.action },
        { withCredentials: true }
      );
      const actionLabel = GM_ACTIONS.find(a => a.key === confirm.action)?.label || confirm.action;
      setActionFeedback({ ok: true, msg: `"${actionLabel}" activado para ${confirm.name}. Se aplicará en su próximo login.` });
    } catch (err) {
      setActionFeedback({ ok: false, msg: err?.response?.data?.detail || 'Error al aplicar la acción.' });
    } finally {
      setApplying(false);
      setConfirm(null);
      setTimeout(() => setActionFeedback(null), 4000);
    }
  };

  const restoreChar = async () => {
    if (!restoreConfirm) return;
    setRestoring(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/characters/${restoreConfirm.guid}/restore`, {}, { withCredentials: true });
      setActionFeedback({ ok: true, msg: `Personaje "${restoreConfirm.name}" restaurado correctamente.` });
      setDeletedChars(prev => prev.filter(c => c.guid !== restoreConfirm.guid));
      reload();
    } catch (err) {
      setActionFeedback({ ok: false, msg: err?.response?.data?.detail || 'Error al restaurar el personaje.' });
    } finally {
      setRestoring(false);
      setRestoreConfirm(null);
      setTimeout(() => setActionFeedback(null), 4000);
    }
  };

  const generateTempPassword = async () => {
    if (!selected?.username) return;
    setGeneratingTempPwd(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/accounts/${encodeURIComponent(selected.username)}/temp-password`,
        {}, { withCredentials: true }
      );
      setTempPwdResult(data);
    } catch (err) {
      setActionFeedback({ ok: false, msg: err?.response?.data?.detail || 'Error al generar la contraseña temporal.' });
      setTimeout(() => setActionFeedback(null), 4000);
    } finally {
      setGeneratingTempPwd(false);
      setTempPwdConfirm(false);
    }
  };

  const statCard = (icon, label, value, color = '#00e5ff') => (
    <div style={{
      flex: 1, minWidth: 100, textAlign: 'center',
      background: 'rgba(0,0,0,0.2)', borderRadius: 10, padding: '14px 10px',
      border: '1px solid rgba(255,255,255,0.05)',
    }}>
      <div style={{ color, marginBottom: 4 }}>{icon}</div>
      <div style={{ color: '#fff', fontWeight: 800, fontSize: 18 }}>{value}</div>
      <div style={{ color: 'rgba(224,247,255,0.35)', fontSize: 10, letterSpacing: 1, marginTop: 2 }}>{label}</div>
    </div>
  );

  const totals = chars.reduce((acc, c) => ({
    played:       acc.played       + c.played_secs,
    hk:           acc.hk           + c.honorable_kills,
    quests:       acc.quests       + c.quests_done,
    achievements: acc.achievements + c.achievements_done,
    gold:         acc.gold         + c.gold,
  }), { played: 0, hk: 0, quests: 0, achievements: 0, gold: 0 });

  const acc = data?.account;
  const game = data?.game;
  const tot = data?.totals;
  const events = (data?.timeline || []).filter((e) => (filter === 'all' ? !e.duplicate : e.kind === filter));

  const renderEvent = (e) => {
    if (e.kind === 'topup') return {
      desc: <>Recarga de cristales <span className="adm-mut adm-mono">· {e.ref ? `…${e.ref}` : 'Stripe'}</span></>,
      amount: <><b style={{ color: '#fcd34d' }}>+{fmtNum(e.crystals)} ❄</b> <span className="adm-mut">· {fmtEur(e.eur)}</span></>,
    };
    if (e.kind === 'purchase') {
      const d = DELIVERY[e.delivery?.status] || DELIVERY.unknown;
      return {
        desc: <>{e.product_name || e.product_id}{e.character_name && <span className="adm-mut"> → {e.character_name}</span>}</>,
        amount: e.voucher_used ? <b style={{ color: '#fcd34d' }}>bono</b> : <b style={{ color: '#00e5ff' }}>−{fmtNum(e.price_crystals)} ❄</b>,
        status: <span style={{ color: d.color, fontWeight: 700 }} title={d.hint}>{d.label}</span>,
        detail: (
          <>
            {d.hint && <div style={{ color: d.color }}>{d.hint}</div>}
            {e.delivery?.detail && <div><span className="adm-mut">Worldserver:</span> {e.delivery.detail}</div>}
            <div><span className="adm-mut">Resultado:</span> <span className="adm-mono" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{e.action_result || '—'}</span></div>
          </>
        ),
      };
    }
    if (e.kind === 'crystals') return {
      desc: <>{REASON_LABELS[e.reason] || e.reason}{e.actor && <span className="adm-mut"> · por {e.actor}</span>}{e.ref && <span className="adm-mut adm-mono"> · {e.ref}</span>}</>,
      amount: <b style={{ color: e.delta >= 0 ? '#a78bfa' : '#f87171' }}>{signed(e.delta)} ❄</b>,
      status: e.balance_after != null ? <span className="adm-mut">saldo {fmtNum(e.balance_after)}</span> : null,
    };
    return {
      desc: <>{VOUCHER_REASONS[e.reason] || e.reason}: {VOUCHER_LABELS[e.product] || e.product}{e.actor && <span className="adm-mut"> · por {e.actor}</span>}{e.ref && <span className="adm-mut"> · {e.ref}</span>}</>,
      amount: <b style={{ color: e.delta >= 0 ? '#fb923c' : '#f87171' }}>{signed(e.delta)} bono{Math.abs(e.delta) === 1 ? '' : 's'}</b>,
    };
  };

  const kpi = (label, value, sub, color = '#e0f7ff') => (
    <div className="adm-kpi">
      <span className="adm-kpi-lbl">{label}</span>
      <span className="adm-kpi-val" style={{ color }}>{value}</span>
      {sub && <span className="adm-kpi-sub">{sub}</span>}
    </div>
  );

  return (
    <div className="admin-section anim-fade-up" style={{ animationDelay: '0.1s' }}>
      <div className="admin-section-header">
        <h2 className="admin-section-title">Ficha de cuenta</h2>
        {selected && (
          <button type="button" className="adm-btn adm-btn--ghost" onClick={reload} disabled={loading} title="Volver a cargar la cuenta">
            <RefreshCw size={13} /> Recargar
          </button>
        )}
      </div>

      {/* Modal de confirmación */}
      {confirm && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 200,
          background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: 'rgba(5,15,35,0.98)', border: '1px solid rgba(0,229,255,0.25)',
            borderRadius: 14, padding: '28px 32px', maxWidth: 420, width: '90%',
          }}>
            <p style={{ color: 'rgba(224,247,255,0.5)', fontSize: 11, fontWeight: 700, letterSpacing: 1.5, margin: '0 0 10px' }}>
              CONFIRMAR ACCIÓN GM
            </p>
            <p style={{ color: '#e0e8f0', fontSize: 15, fontWeight: 700, margin: '0 0 8px' }}>
              {GM_ACTIONS.find(a => a.key === confirm.action)?.label}
            </p>
            <p style={{ color: 'rgba(224,247,255,0.55)', fontSize: 13, margin: '0 0 6px' }}>
              Personaje: <strong style={{ color: '#00e5ff' }}>{confirm.name}</strong>
            </p>
            <p style={{ color: 'rgba(224,247,255,0.4)', fontSize: 12, margin: '0 0 22px' }}>
              {GM_ACTIONS.find(a => a.key === confirm.action)?.desc}
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirm(null)}
                disabled={applying}
                style={{
                  padding: '9px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                  background: 'transparent', border: '1px solid rgba(255,255,255,0.12)',
                  color: 'rgba(224,247,255,0.5)', cursor: 'pointer',
                }}
              >
                Cancelar
              </button>
              <button
                onClick={applyAtLogin}
                disabled={applying}
                style={{
                  padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                  background: 'rgba(0,229,255,0.12)', border: '1px solid rgba(0,229,255,0.35)',
                  color: '#00e5ff', cursor: applying ? 'not-allowed' : 'pointer',
                }}
              >
                {applying ? 'Aplicando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feedback de acción GM */}
      {actionFeedback && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px',
          borderRadius: 8, marginBottom: 16,
          background: actionFeedback.ok ? 'rgba(52,211,153,0.1)' : 'rgba(248,113,113,0.1)',
          border: `1px solid ${actionFeedback.ok ? 'rgba(52,211,153,0.3)' : 'rgba(248,113,113,0.3)'}`,
          color: actionFeedback.ok ? '#34d399' : '#f87171', fontSize: 13,
        }}>
          {actionFeedback.ok ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
          {actionFeedback.msg}
        </div>
      )}

      {/* Buscador */}
      <div style={{ position: 'relative', marginBottom: 18, maxWidth: 560 }}>
        <Search size={15} style={{ position: 'absolute', left: 12, top: 18, transform: 'translateY(-50%)', color: 'rgba(0,229,255,0.5)', pointerEvents: 'none' }} />
        <input
          className="adm-input" style={{ width: '100%', height: 36, paddingLeft: 34, fontSize: 14 }}
          value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={onSearchKey}
          onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
          onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
          placeholder="Cuenta, email, cuenta de juego o personaje… (Intro para abrir)"
          aria-label="Buscar cuenta" data-testid="account-search" autoComplete="off"
        />
        {showDropdown && suggestions.length > 0 && (
          <div role="listbox" className="adm-suggest">
            {suggestions.map((s, i) => (
              <button key={s.id} type="button" role="option" aria-selected={i === activeIdx}
                className={`adm-suggest-item${i === activeIdx ? ' adm-suggest-item--active' : ''}`}
                onMouseDown={() => loadAccount(s.username)} onMouseEnter={() => setActiveIdx(i)}>
                <Users size={12} style={{ color: 'rgba(0,229,255,0.5)', flexShrink: 0 }} />
                <b>{s.username}</b>
                <span className="adm-mut" style={{ fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis' }}>· {s.match}{s.match === 'email' && s.email ? ` (${s.email})` : ''}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {loading && <p className="adm-mut" style={{ fontSize: 13 }}>Cargando cuenta…</p>}
      {loadError && !loading && (
        <div className="register-error" style={{ marginBottom: 16 }}><AlertCircle size={15} /><span>{loadError}</span></div>
      )}
      {!selected && !loading && (
        <p className="adm-mut" style={{ fontSize: 13 }}>Busca una cuenta, o pulsa su nombre en Usuarios o Compras.</p>
      )}

      {acc && !loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }} data-testid="account-sheet">
          {/* Cabecera */}
          <div className="adm-card">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                <span style={{ fontSize: 20, fontWeight: 800, color: acc.role === 'admin' ? '#FFD700' : '#e0f7ff' }}>{acc.username}</span>
                {game.online
                  ? <span className="adm-chip" style={{ color: '#4ade80', borderColor: 'rgba(74,222,128,0.4)' }}>● en línea</span>
                  : <span className="adm-chip">desconectado</span>}
                <span className={`user-role-badge ${acc.role === 'admin' ? 'user-role-badge--admin' : ''}`}>{acc.role}</span>
                {game.banned && (
                  <span className="adm-chip" style={{ color: '#f87171', borderColor: 'rgba(248,113,113,0.45)' }}
                    title={`Por ${game.banned_by || '—'}`}>
                    <Ban size={11} /> baneada{game.unban_at ? ` hasta ${fmtDateTime(game.unban_at)}` : ' (permanente)'}{game.ban_reason ? ` · ${game.ban_reason}` : ''}
                  </span>
                )}
                {!game.exists && <span className="adm-chip" style={{ color: '#fbbf24' }}>sin cuenta de juego</span>}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {game.exists && (
                  <button type="button" className="adm-btn"
                    style={game.banned ? { color: '#34d399', borderColor: 'rgba(52,211,153,0.4)' } : { color: '#f87171', borderColor: 'rgba(248,113,113,0.35)' }}
                    onClick={() => setBanModal({ username: acc.username, isBanned: game.banned, banReason: game.ban_reason })}>
                    <Ban size={13} /> {game.banned ? 'Desbanear' : 'Banear'}
                  </button>
                )}
                <button type="button" className="adm-btn" onClick={() => setTempPwdConfirm(true)}>
                  <KeyRound size={13} /> Contraseña temporal
                </button>
              </div>
            </div>
            <div className="adm-facts">
              <span><span className="adm-mut">Email</span> {acc.email} {acc.email_verified ? <span style={{ color: '#34d399' }} title="Verificado">✓</span> : <span style={{ color: '#fbbf24', fontSize: 11 }}>(sin verificar)</span>}</span>
              <span><span className="adm-mut">Cuenta de juego</span> <span className="adm-mono">{game.account}</span></span>
              <span><span className="adm-mut">Registro</span> {fmtDateTime(acc.created_at)}{acc.migrated && <span className="adm-mut"> (migrada)</span>}</span>
              <span><span className="adm-mut">Último login</span> {fmtDateTime(game.last_login, 'nunca')}</span>
              <span><span className="adm-mut">IP</span> <span className="adm-mono">{game.last_ip && game.last_ip !== '0.0.0.0' ? game.last_ip : '—'}</span> {game.last_ip && game.last_ip !== '0.0.0.0' && <GeoCell ip={game.last_ip} />}</span>
              {acc.referred_by && <span><span className="adm-mut">Invitado por</span> <button type="button" className="adm-link" onClick={() => loadAccount(acc.referred_by)}>{acc.referred_by}</button></span>}
            </div>
          </div>

          {tot.failed_deliveries > 0 && (
            <div className="admin-feedback admin-feedback--err" style={{ margin: 0 }}>
              <AlertCircle size={15} />
              <span>{tot.failed_deliveries} {tot.failed_deliveries === 1 ? 'compra cobrada cuya entrega falló' : 'compras cobradas cuya entrega falló'} en el worldserver. Revisa el historial y devuelve el pago a mano.</span>
            </div>
          )}

          {/* Totales */}
          <div className="adm-kpis">
            {kpi('Saldo', `${fmtNum(acc.frost_crystals)} ❄`, null, '#00e5ff')}
            {kpi('Pagado', fmtEur(tot.paid_eur), `${tot.topups} ${tot.topups === 1 ? 'recarga' : 'recargas'} · ${fmtNum(tot.crystals_bought)} ❄`, '#fcd34d')}
            {kpi('Gastado en tienda', `${fmtNum(tot.crystals_spent)} ❄`, `${tot.purchases} ${tot.purchases === 1 ? 'compra' : 'compras'}${tot.voucher_redeems ? ` · ${tot.voucher_redeems} con bono` : ''}`)}
            {kpi('Bonos sin canjear', voucherText(acc.vouchers) || 'ninguno', null, voucherText(acc.vouchers) ? '#fb923c' : 'rgba(224,247,255,0.45)')}
            {kpi('Entregas', tot.pending_deliveries || tot.failed_deliveries ? `${tot.pending_deliveries} pend. · ${tot.failed_deliveries} ${tot.failed_deliveries === 1 ? 'fallida' : 'fallidas'}` : 'todo al día',
              null, tot.failed_deliveries ? '#f87171' : tot.pending_deliveries ? '#fbbf24' : '#34d399')}
            {kpi('Votos', fmtNum(tot.votes))}
          </div>

          {/* Personajes */}
          {/* Resultados */}
          {chars.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

              {/* Resumen global */}
              <div style={{
                background: 'rgba(0,229,255,0.04)', border: '1px solid rgba(0,229,255,0.15)',
                borderRadius: 12, padding: '16px 20px',
              }}>
                <div style={{ marginBottom: 14 }}>
                  <p style={{ color: 'rgba(224,247,255,0.5)', fontSize: 10, fontWeight: 700, letterSpacing: 1.5, margin: 0 }}>
                    PERSONAJES · {chars.length}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {statCard(<Clock size={16} />,      'TIEMPO JUGADO', formatPlayed(totals.played),         '#00e5ff')}
                  {statCard(<Sword size={16} />,      'KILLS PVP',     totals.hk.toLocaleString(),          '#f87171')}
                  {statCard(<ScrollText size={16} />, 'QUESTS',        totals.quests.toLocaleString(),      '#fbbf24')}
                  {statCard(<Trophy size={16} />,     'LOGROS',        totals.achievements.toLocaleString(),'#a78bfa')}
                  {statCard(<Coins size={16} />,      'ORO TOTAL',     `${totals.gold.toLocaleString()}g`,  '#fcd34d')}
                </div>
              </div>

              {/* Detalle por personaje */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {chars.map((c) => {
                  const color = STAT_CLASS_COLORS[c.class] || '#aaa';
                  return (
                    <div key={c.guid} style={{
                      background: 'rgba(0,0,0,0.25)', border: `1px solid ${color}33`,
                      borderRadius: 12, padding: '14px 18px',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
                        <a href={`/armeria/${encodeURIComponent(c.name)}`} target="_blank" rel="noopener noreferrer" title="Abrir en la armería"
                          style={{ color, fontWeight: 700, fontSize: 15, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          {c.name} <ExternalLink size={11} style={{ opacity: 0.5 }} />
                        </a>
                        <span style={{ color: 'rgba(224,247,255,0.35)', fontSize: 12 }}>
                          Nv.{c.level} · {STAT_RACE_NAMES[c.race] || `Raza ${c.race}`} · {STAT_CLASS_NAMES[c.class] || `Clase ${c.class}`}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {[
                          { icon: <Clock size={13} />,      label: 'Jugado',    val: formatPlayed(c.played_secs),          color: '#00e5ff' },
                          { icon: <Sword size={13} />,      label: 'Kills PvP', val: c.honorable_kills.toLocaleString(),   color: '#f87171' },
                          { icon: <ScrollText size={13} />, label: 'Quests',    val: c.quests_done.toLocaleString(),       color: '#fbbf24' },
                          { icon: <Trophy size={13} />,     label: 'Logros',    val: c.achievements_done.toLocaleString(), color: '#a78bfa' },
                          { icon: <Coins size={13} />,      label: 'Oro',       val: `${c.gold.toLocaleString()}g ${c.silver}s`, color: '#fcd34d' },
                        ].map(({ icon, label, val, color: col }) => (
                          <div key={label} style={{
                            display: 'flex', alignItems: 'center', gap: 5,
                            background: 'rgba(255,255,255,0.04)', borderRadius: 8,
                            padding: '6px 12px', border: '1px solid rgba(255,255,255,0.06)',
                          }}>
                            <span style={{ color: col }}>{icon}</span>
                            <span style={{ color: 'rgba(224,247,255,0.4)', fontSize: 11 }}>{label}:</span>
                            <span style={{ color: '#e0e8f0', fontWeight: 600, fontSize: 12 }}>{val}</span>
                          </div>
                        ))}
                      </div>

                      {/* Acciones GM */}
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                        {GM_ACTIONS.map((action) => (
                          <button
                            key={action.key}
                            onClick={() => setConfirm({ guid: c.guid, name: c.name, action: action.key })}
                            style={{
                              padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                              background: `${action.color}14`, border: `1px solid ${action.color}44`,
                              color: action.color, cursor: 'pointer',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = `${action.color}28`; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = `${action.color}14`; }}
                          >
                            {action.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {chars.length === 0 && (
            <p className="adm-mut" style={{ fontSize: 13, margin: 0 }}>Esta cuenta no tiene personajes activos.</p>
          )}

          {/* Personajes eliminados */}
          {deletedChars.length > 0 && (
            <div>
              <p style={{ color: 'rgba(224,247,255,0.4)', fontSize: 11, fontWeight: 700, letterSpacing: 1.5, margin: '0 0 12px' }}>
                PERSONAJES ELIMINADOS ({deletedChars.length})
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {deletedChars.map(c => {
                  const color = STAT_CLASS_COLORS[c.class] || '#aaa';
                  return (
                    <div key={c.guid} style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'rgba(248,113,113,0.04)', border: '1px solid rgba(248,113,113,0.12)', borderRadius: 10, padding: '10px 16px' }}>
                      <div style={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0 }} />
                      <span style={{ color, fontWeight: 700, fontSize: 14 }}>{c.name}</span>
                      <span style={{ color: 'rgba(224,247,255,0.35)', fontSize: 12 }}>
                        Nv.{c.level} · {STAT_CLASS_NAMES[c.class] || `Clase ${c.class}`}
                      </span>
                      <span style={{ color: 'rgba(248,113,113,0.5)', fontSize: 11, marginLeft: 'auto' }}>Eliminado: {fmtDateTime(c.deleted_at)}</span>
                      <button
                        onClick={() => setRestoreConfirm({ guid: c.guid, name: c.name })}
                        style={{ padding: '4px 12px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.3)', color: '#34d399', cursor: 'pointer', flexShrink: 0 }}
                      >
                        Restaurar
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Historial */}
          <div className="adm-card" data-testid="account-history">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <span className="adm-card-title">HISTORIAL</span>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {FILTERS.map(([k, label]) => {
                  const n = k === 'all' ? data.timeline.filter((e) => !e.duplicate).length : data.timeline.filter((e) => e.kind === k).length;
                  return (
                    <button key={k} type="button" className={`adm-btn${filter === k ? ' adm-btn--active' : ''}`} onClick={() => { setFilter(k); setOpenEvent(null); }}>
                      {label} <span className="adm-mut">{n}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            {filter === 'crystals' && (
              <p className="adm-mut" style={{ fontSize: 11.5, margin: '0 0 10px' }}>
                Registro de cristales (ledger), disponible desde el 09/10/2026. En «Todo» no se repiten las recargas ni las compras.
              </p>
            )}
            {events.length === 0 ? (
              <p className="adm-mut" style={{ fontSize: 13, margin: 0 }}>Sin movimientos.</p>
            ) : (
              <div className="adm-timeline">
                {events.map((e, i) => {
                  const r = renderEvent(e);
                  const chip = KIND_CHIP[e.kind];
                  const open = openEvent === i;
                  return (
                    <React.Fragment key={`${e.kind}-${e.at}-${i}`}>
                      <div className={`adm-tl-row${r.detail ? ' adm-tl-row--click' : ''}`} onClick={() => r.detail && setOpenEvent(open ? null : i)}>
                        <span className="adm-mut adm-tl-date">{fmtDateTime(e.at)}</span>
                        <span className="adm-chip" style={{ color: chip.color, borderColor: `${chip.color}55` }}>{chip.label}</span>
                        <span className="adm-tl-desc">{r.desc}</span>
                        <span className="adm-tl-amount">{r.amount}</span>
                        <span className="adm-tl-status">{r.status}{r.detail && (open ? <ChevronDown size={13} /> : <ChevronRight size={13} />)}</span>
                      </div>
                      {open && r.detail && <div className="adm-tl-detail">{r.detail}</div>}
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal confirmar contraseña temporal */}
      {tempPwdConfirm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'rgba(5,15,35,0.98)', border: '1px solid rgba(0,229,255,0.25)', borderRadius: 14, padding: '28px 32px', maxWidth: 420, width: '90%' }}>
            <p style={{ color: 'rgba(224,247,255,0.5)', fontSize: 11, fontWeight: 700, letterSpacing: 1.5, margin: '0 0 10px' }}>
              GENERAR CONTRASEÑA TEMPORAL
            </p>
            <p style={{ color: '#e0e8f0', fontSize: 15, fontWeight: 700, margin: '0 0 8px' }}>
              Cuenta: <span style={{ color: '#00e5ff' }}>{selected?.username}</span>
            </p>
            <p style={{ color: 'rgba(224,247,255,0.4)', fontSize: 12, margin: '0 0 22px', lineHeight: 1.5 }}>
              Se sustituye la contraseña actual (web y juego) por una nueva temporal.
              El jugador podrá entrar con ella y cambiarla desde su cuenta, sin necesidad de correo.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setTempPwdConfirm(false)}
                disabled={generatingTempPwd}
                style={{
                  padding: '9px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                  background: 'transparent', border: '1px solid rgba(255,255,255,0.12)',
                  color: 'rgba(224,247,255,0.5)', cursor: 'pointer',
                }}
              >
                Cancelar
              </button>
              <button
                onClick={generateTempPassword}
                disabled={generatingTempPwd}
                style={{
                  padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                  background: 'rgba(0,229,255,0.12)', border: '1px solid rgba(0,229,255,0.35)',
                  color: '#00e5ff', cursor: generatingTempPwd ? 'default' : 'pointer',
                  opacity: generatingTempPwd ? 0.6 : 1,
                }}
              >
                {generatingTempPwd ? 'Generando…' : 'Generar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal resultado: mostrar la contraseña temporal generada */}
      {tempPwdResult && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'rgba(5,15,35,0.98)', border: '1px solid rgba(0,229,255,0.25)', borderRadius: 14, padding: '28px 32px', maxWidth: 440, width: '90%' }}>
            <p style={{ color: 'rgba(224,247,255,0.5)', fontSize: 11, fontWeight: 700, letterSpacing: 1.5, margin: '0 0 10px' }}>
              CONTRASEÑA TEMPORAL · {tempPwdResult.username}
            </p>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
              background: 'rgba(0,229,255,0.06)', border: '1px solid rgba(0,229,255,0.25)',
              borderRadius: 8, padding: '14px 16px', margin: '14px 0 10px',
            }}>
              <span style={{ color: '#00e5ff', fontSize: 20, fontWeight: 700, fontFamily: 'monospace', letterSpacing: 1 }}>
                {tempPwdResult.temp_password}
              </span>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(tempPwdResult.temp_password);
                  setTempPwdCopied(true);
                  setTimeout(() => setTempPwdCopied(false), 2000);
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '6px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)',
                  color: 'rgba(224,247,255,0.7)', cursor: 'pointer', flexShrink: 0,
                }}
              >
                <Copy size={12} /> {tempPwdCopied ? 'Copiado' : 'Copiar'}
              </button>
            </div>
            <p style={{ color: 'rgba(224,247,255,0.4)', fontSize: 12, margin: '0 0 22px', lineHeight: 1.5 }}>
              Compártela con el jugador por un canal de confianza (Discord, etc.). No se
              guarda en texto plano y no volverá a mostrarse — si la pierdes, genera otra.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setTempPwdResult(null); setTempPwdCopied(false); }}
                style={{
                  padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                  background: 'rgba(0,229,255,0.12)', border: '1px solid rgba(0,229,255,0.35)',
                  color: '#00e5ff', cursor: 'pointer',
                }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal restaurar personaje */}
      {restoreConfirm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'rgba(5,15,35,0.98)', border: '1px solid rgba(0,229,255,0.25)', borderRadius: 14, padding: '28px 32px', maxWidth: 400, width: '90%' }}>
            <p style={{ color: 'rgba(224,247,255,0.5)', fontSize: 11, fontWeight: 700, letterSpacing: 1.5, margin: '0 0 10px' }}>RESTAURAR PERSONAJE</p>
            <p style={{ color: '#e0e8f0', fontSize: 15, fontWeight: 700, margin: '0 0 8px' }}>{restoreConfirm.name}</p>
            <p style={{ color: 'rgba(224,247,255,0.4)', fontSize: 13, margin: '0 0 22px' }}>El personaje volverá a estar activo en la cuenta. ¿Confirmas?</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setRestoreConfirm(null)} disabled={restoring} style={{ padding: '9px 18px', borderRadius: 8, fontSize: 13, background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(224,247,255,0.5)', cursor: 'pointer' }}>Cancelar</button>
              <button onClick={restoreChar} disabled={restoring} style={{ padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700, background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.35)', color: '#34d399', cursor: restoring ? 'not-allowed' : 'pointer' }}>
                {restoring ? 'Restaurando...' : 'Restaurar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {banModal && (
        <BanDialog
          banModal={banModal}
          showFeedback={showFeedback}
          onClose={() => setBanModal(null)}
          onDone={() => { setBanModal(null); reload(); }}
        />
      )}
    </div>
  );
}
